import { LoadingIndicator, LoadingState, Alert, Button, EmptyState, StatusBadge, Surface } from './ui/Primitives';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SUPPORTED_INDUSTRIES } from '../utils/industries';


const SOURCE_KIND_LABELS = {
  file: 'Document',
  url: 'Website',
  note: 'Note',
};

export const KNOWLEDGE_SUGGESTION_FIELDS = [
  { key: 'businessWebsite', label: 'Website', organizationKey: 'business_website', sectionKey: 'general' },
  { key: 'businessName', label: 'Business name', organizationKey: 'business_name', sectionKey: 'general' },
  { key: 'businessLocation', label: 'Location', organizationKey: 'business_location', sectionKey: 'hours_location' },
  { key: 'businessPhoneNumber', label: 'Phone number', organizationKey: 'business_phone_number', sectionKey: 'hours_location' },
  { key: 'businessHours', label: 'Hours', organizationKey: 'business_hours', sectionKey: 'hours_location', multiline: true },
  { key: 'businessServices', label: 'Services', organizationKey: 'business_services', sectionKey: 'services_pricing', multiline: true },
  { key: 'businessBio', label: 'About the business', organizationKey: 'business_bio', sectionKey: 'general', multiline: true },
  { key: 'staffBio', label: 'Team', organizationKey: 'staff_bio', sectionKey: 'team', multiline: true },
  { key: 'businessFaq', label: 'FAQs', organizationKey: 'business_faq', sectionKey: 'policies_faqs', multiline: true },
  { key: 'industry', label: 'Industry', organizationKey: 'industry', sectionKey: 'general', options: SUPPORTED_INDUSTRIES },
  { key: 'timezone', label: 'Timezone', organizationKey: 'timezone', sectionKey: 'hours_location' },
];


function useDialogKeyboard(onClose, locked = false) {
  const dialogRef = useRef(null);
  const lockedRef = useRef(locked);
  lockedRef.current = locked;

  useEffect(() => {
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    if (!dialog) return undefined;
    dialog.focus();

    function handleKeyDown(event) {
      if (event.key === 'Escape' && !lockedRef.current) {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(dialog.querySelectorAll(
        'button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
      )).filter(element => element.offsetParent !== null);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    dialog.addEventListener('keydown', handleKeyDown);
    return () => {
      dialog.removeEventListener('keydown', handleKeyDown);
      if (previousFocus?.focus) previousFocus.focus();
    };
  }, [onClose]);

  return dialogRef;
}


export function knowledgeErrorMessage(error, fallback = 'That change could not be completed.') {
  return error?.response?.data?.error?.message || fallback;
}


function statusTone(source) {
  if (!source.enabled) return 'neutral';
  if (source.status === 'ready' && ['queued', 'processing'].includes(source.latestVersionStatus)) return 'processing';
  if (source.status === 'ready' && source.latestVersionStatus === 'failed') return 'warning';
  if (source.status === 'ready') return 'success';
  if (source.status === 'failed') return 'danger';
  return 'processing';
}


function statusLabel(source) {
  if (!source.enabled) return 'Disabled';
  if (source.status === 'ready' && source.latestVersionStatus === 'queued') return 'Ready · update queued';
  if (source.status === 'ready' && source.latestVersionStatus === 'processing') return 'Ready · updating';
  if (source.status === 'ready' && source.latestVersionStatus === 'failed') return 'Ready · update failed';
  if (source.status === 'queued') return 'Queued';
  if (source.status === 'processing') return 'Processing';
  if (source.status === 'failed') return 'Needs attention';
  return 'Ready';
}


function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(Math.round(bytes / 1024), 1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


function formatDate(value) {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: parsed.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  });
}


function KnowledgeBusy({ label, className = '' }) {
  return <LoadingIndicator className={className} label={label} />;
}


export function KnowledgeSourceComposer({ capability, onClose, onCreate }) {
  const initialKind = capability?.fileUploadsEnabled ? 'file' : 'url';
  const [kind, setKind] = useState(initialKind);
  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useDialogKeyboard(onClose, saving);

  async function submit(event) {
    event.preventDefault();
    setError('');
    setSaving(true);
    try {
      await onCreate(kind, {
        files,
        title,
        url,
        content,
      });
      onClose();
    } catch (requestError) {
      setError(knowledgeErrorMessage(requestError, 'The source could not be added.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="knowledge-modal-backdrop" role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget && !saving) onClose();
    }}>
      <section ref={dialogRef} tabIndex="-1" className="knowledge-modal" role="dialog" aria-modal="true" aria-labelledby="add-source-title" aria-describedby={error ? 'knowledge-source-error' : undefined}>
        <div className="knowledge-modal-header">
          <div><span className="say-eyebrow">Supporting knowledge</span><h2 id="add-source-title">Add a source</h2></div>
          <button type="button" className="knowledge-modal-close" aria-label="Close" onClick={onClose} disabled={saving}>×</button>
        </div>
        <form onSubmit={submit} aria-busy={saving}>
          <div className="knowledge-source-type-tabs" role="tablist" aria-label="Source type">
            <button type="button" role="tab" aria-selected={kind === 'file'} className={kind === 'file' ? 'active' : ''} disabled={saving || !capability?.fileUploadsEnabled} onClick={() => setKind('file')}>Documents</button>
            <button type="button" role="tab" aria-selected={kind === 'url'} className={kind === 'url' ? 'active' : ''} disabled={saving} onClick={() => setKind('url')}>Website</button>
            <button type="button" role="tab" aria-selected={kind === 'note'} className={kind === 'note' ? 'active' : ''} disabled={saving} onClick={() => setKind('note')}>Note</button>
          </div>
          {kind === 'file' ? (
            <div className="knowledge-composer-field"><span>Choose documents</span><small>PDF, Word, text, Markdown, or CSV.</small><label className={`knowledge-file-picker ${files.length ? 'has-files' : ''} ${saving ? 'disabled' : ''}`}><span className="knowledge-file-button"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14v4.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V14" /></svg>Select documents</span><span className="knowledge-file-summary">{files.length ? `${files.length} selected · ${files.map(file => file.name).join(', ')}` : 'No documents selected'}</span><input className="knowledge-file-input" type="file" multiple accept=".pdf,.docx,.txt,.md,.csv" required disabled={saving} onChange={event => setFiles(Array.from(event.target.files || []))} /></label></div>
          ) : null}
          {kind === 'url' ? <>
            <label className="knowledge-composer-field"><span>Website URL</span><input type="text" inputMode="url" autoCapitalize="none" spellCheck="false" required value={url} disabled={saving} placeholder="example.com/menu" onChange={event => setUrl(event.target.value)} /></label>
            <label className="knowledge-composer-field"><span>Source name <small>optional</small></span><input value={title} disabled={saving} placeholder="Current menu" onChange={event => setTitle(event.target.value)} /></label>
          </> : null}
          {kind === 'note' ? <>
            <label className="knowledge-composer-field"><span>Note title</span><input required value={title} disabled={saving} placeholder="Cancellation policy" onChange={event => setTitle(event.target.value)} /></label>
            <label className="knowledge-composer-field"><span>What should your Receptionist know?</span><textarea required rows="7" value={content} disabled={saving} placeholder="Write the approved information callers should receive…" onChange={event => setContent(event.target.value)} /></label>
          </> : null}
          {error ? <div id="knowledge-source-error"><Alert tone="danger">{error}</Alert></div> : null}
          <div className="knowledge-modal-actions">
            <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" loading={saving} loadingLabel="Adding source…" disabled={kind === 'file' && !files.length}>Add source</Button>
          </div>
        </form>
      </section>
    </div>
  );
}


function SourceRow({ source, canManage, busy, busyLabel, onUpdate, onReingest, onEditNote, onRefresh, onDelete, onReviewSuggestions }) {
  const processing = ['queued', 'processing'].includes(source.latestVersionStatus);
  const [draftTitle, setDraftTitle] = useState(source.title);

  useEffect(() => setDraftTitle(source.title), [source.title]);

  async function reingest() {
    await onReingest(source.id);
  }

  return (
    <li className="knowledge-source-row">
      <details className="knowledge-source-disclosure">
        <summary className="knowledge-source-summary">
          <div className={`knowledge-source-kind kind-${source.kind}`} aria-hidden="true">{source.kind === 'file' ? 'DOC' : source.kind === 'url' ? 'WEB' : 'TXT'}</div>
          <div className="knowledge-source-main">
            <div className="knowledge-source-title-line"><strong>{source.title}</strong></div>
            <div className="knowledge-source-meta">
              <StatusBadge status={statusTone(source)}>{statusLabel(source)}</StatusBadge>
              {busy ? <KnowledgeBusy label={busyLabel || 'Working…'} /> : formatDate(source.updatedAt) ? <span>Updated {formatDate(source.updatedAt)}</span> : null}
            </div>
          </div>
          <span className="knowledge-source-chevron" aria-hidden="true">⌄</span>
        </summary>
        <div className="knowledge-source-details">
          <div className="knowledge-source-detail-meta"><span><strong>Type</strong>{SOURCE_KIND_LABELS[source.kind]}</span>{formatBytes(source.byteSize) ? <span><strong>Size</strong>{formatBytes(source.byteSize)}</span> : null}</div>
          {source.lastErrorCode ? <small className="knowledge-source-error">The latest update needs attention ({source.lastErrorCode}). The last ready version remains available when possible.</small> : null}
          {canManage ? <div className="knowledge-source-title-editor"><label><span>Source name</span><input value={draftTitle} maxLength="240" disabled={busy} onChange={event => setDraftTitle(event.target.value)} /></label><Button variant="secondary" size="sm" disabled={busy || !draftTitle.trim() || draftTitle.trim() === source.title} onClick={() => onUpdate(source.id, { title: draftTitle.trim() })}>Save name</Button></div> : null}
          {canManage ? <div className="knowledge-source-actions">
            {source.suggestionCount > 0 ? <Button variant="quiet" size="sm" onClick={() => onReviewSuggestions(source)} disabled={busy}>Review {source.suggestionCount} suggestion{source.suggestionCount === 1 ? '' : 's'}</Button> : null}
            {source.kind === 'file' ? <Button variant="secondary" size="sm" onClick={reingest} disabled={busy || processing}>Reprocess</Button> : null}
            {source.kind === 'note' ? <Button variant="secondary" size="sm" onClick={() => onEditNote(source)} disabled={busy || processing}>Edit note</Button> : null}
            {source.kind === 'url' ? <Button variant="secondary" size="sm" onClick={() => onRefresh(source.id)} disabled={busy || processing}>Refresh</Button> : null}
            <Button variant="secondary" size="sm" onClick={() => onUpdate(source.id, { enabled: !source.enabled })} disabled={busy}>{source.enabled ? 'Disable' : 'Enable'}</Button>
            <Button variant="danger" size="sm" onClick={() => onDelete(source)} disabled={busy}>Delete</Button>
          </div> : null}
        </div>
      </details>
    </li>
  );
}


export function KnowledgeSourceLibrary({
  sources,
  loading,
  capability,
  busySourceId,
  busySourceLabel,
  onAdd,
  onUpdate,
  onReingest,
  onEditNote,
  onRefresh,
  onDelete,
  onReviewSuggestions,
  onTest,
}) {
  const [question, setQuestion] = useState('');
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState('');
  const [result, setResult] = useState(null);

  async function submitTest(event) {
    event.preventDefault();
    const normalizedQuestion = question.trim();
    if (!normalizedQuestion) return;
    setTesting(true);
    setTestError('');
    setResult(null);
    try {
      setResult(await onTest(normalizedQuestion));
    } catch (error) {
      setTestError(knowledgeErrorMessage(error, 'The knowledge test could not be completed.'));
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="knowledge-library">
      <Surface className="knowledge-library-card">
        <div className="knowledge-library-heading">
          <div><span className="say-eyebrow">Supporting knowledge</span><h2>Documents and sources</h2><p>Extend the profile with detailed reference material. Sayable automatically organizes the knowledge it finds.</p></div>
          {capability?.canManage ? <Button onClick={onAdd}>Add source</Button> : null}
        </div>
        {loading ? <KnowledgeBusy label="Loading sources…" className="knowledge-source-loading" /> : sources.length ? (
          <ul className="knowledge-source-list" aria-live="polite">{sources.map(source => (
            <SourceRow
              key={source.id}
              source={source}
              canManage={capability?.canManage}
              busy={busySourceId === source.id}
              busyLabel={busySourceId === source.id ? busySourceLabel : ''}
              onUpdate={onUpdate}
              onReingest={onReingest}
              onEditNote={onEditNote}
              onRefresh={onRefresh}
              onDelete={onDelete}
              onReviewSuggestions={onReviewSuggestions}
            />
          ))}</ul>
        ) : <EmptyState title="No supporting sources yet" description="Upload a menu, add a policy webpage, or write a note without crowding your business-profile fields." action={capability?.canManage ? <Button onClick={onAdd}>Add your first source</Button> : null} />}
      </Surface>
      {capability?.canManage ? <Surface className="knowledge-test-card">
        <span className="say-eyebrow">Try it</span>
        <h2>Ask your Receptionist</h2>
        <p>Preview an answer and see which sources support it.</p>
        <form onSubmit={submitTest} aria-busy={testing}><label htmlFor="knowledge-test-question">Caller question</label><div><input id="knowledge-test-question" value={question} required disabled={testing} placeholder="Do you have a vegetarian lunch menu?" onChange={event => setQuestion(event.target.value)} /><Button type="submit" disabled={testing || !question.trim()} loading={testing} loadingLabel={"Checking sources…"}>{'Test knowledge'}</Button></div></form>
        {testing ? <LoadingState label="Checking sources and preparing the receptionist’s answer…" /> : null}
        {testError ? <Alert tone="danger">{testError}</Alert> : null}
        {result ? <div className="knowledge-test-result" aria-live="polite"><strong>{result.status === 'answered' ? 'Receptionist answer' : result.status === 'conflict' ? 'Conflicting sources found' : 'No reliable answer found'}</strong>{result.answer ? <p>{result.answer}</p> : null}{result.sources?.length ? <div><span>Matched sources</span><ul>{result.sources.map(source => <li key={source.sourceId}>{source.title}{source.page != null ? ` · page ${source.page}` : ''}</li>)}</ul></div> : null}</div> : null}
      </Surface> : null}
    </div>
  );
}


export function KnowledgeSourceDeleteDialog({ source, onClose, onConfirm }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useDialogKeyboard(onClose, deleting);

  async function confirmDelete() {
    setDeleting(true);
    setError('');
    const deleted = await onConfirm(source);
    if (deleted) {
      onClose();
      return;
    }
    setError('We could not delete this source. Please try again.');
    setDeleting(false);
  }

  return (
    <div className="knowledge-modal-backdrop" role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget && !deleting) onClose();
    }}>
      <section
        ref={dialogRef}
        tabIndex="-1"
        className="knowledge-modal knowledge-delete-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-source-title"
        aria-describedby="delete-source-description"
      >
        <div className="knowledge-delete-heading">
          <div className="knowledge-delete-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false"><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" /></svg>
          </div>
          <div>
            <span className="say-eyebrow">Delete source</span>
            <h2 id="delete-source-title">Remove this from Sayable?</h2>
          </div>
        </div>
        <p id="delete-source-description" className="knowledge-delete-description">
          <strong>{source.title}</strong> will be permanently removed. Your Receptionist will no longer use its information when answering callers.
        </p>
        <div className="knowledge-delete-note">This cannot be undone.</div>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <div className="knowledge-modal-actions">
          <Button variant="secondary" onClick={onClose} disabled={deleting}>Keep source</Button>
          <Button className="knowledge-delete-confirm" variant="danger" loading={deleting} loadingLabel="Deleting…" onClick={confirmDelete}>Delete source</Button>
        </div>
      </section>
    </div>
  );
}


export function KnowledgeNoteEditor({ source, initialContent, onClose, onSave }) {
  const [content, setContent] = useState(initialContent || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useDialogKeyboard(onClose, saving);

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onSave(source.id, content);
      onClose();
    } catch (requestError) {
      setError(knowledgeErrorMessage(requestError, 'The note could not be updated.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="knowledge-modal-backdrop" role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget && !saving) onClose();
    }}>
      <section ref={dialogRef} tabIndex="-1" className="knowledge-modal" role="dialog" aria-modal="true" aria-labelledby="edit-note-title" aria-describedby={error ? 'knowledge-note-edit-error' : undefined}>
        <div className="knowledge-modal-header"><div><span className="say-eyebrow">Supporting knowledge</span><h2 id="edit-note-title">Edit {source.title}</h2><p>The current ready version remains available until this update finishes processing.</p></div><button type="button" className="knowledge-modal-close" aria-label="Close" onClick={onClose} disabled={saving}>×</button></div>
        <form onSubmit={submit} aria-busy={saving}>
          <label className="knowledge-composer-field"><span>Approved information</span><textarea required rows="10" value={content} disabled={saving} onChange={event => setContent(event.target.value)} /></label>
          {error ? <div id="knowledge-note-edit-error"><Alert tone="danger">{error}</Alert></div> : null}
          <div className="knowledge-modal-actions"><Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button><Button type="submit" loading={saving} loadingLabel="Saving…" disabled={!content.trim()}>Save new version</Button></div>
        </form>
      </section>
    </div>
  );
}


export function SuggestionReview({ source, organization, suggestions, onClose, onApply }) {
  const availableFields = useMemo(() => KNOWLEDGE_SUGGESTION_FIELDS.filter(field => suggestions?.[field.key]), [suggestions]);
  const [selected, setSelected] = useState(() => availableFields.map(field => field.key));
  const [values, setValues] = useState(() => Object.fromEntries(availableFields.map(field => [field.key, suggestions[field.key]])));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useDialogKeyboard(onClose, saving);

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onApply(source.id, Object.fromEntries(selected.map(key => [key, values[key]])));
      onClose();
    } catch (requestError) {
      setError(knowledgeErrorMessage(requestError, 'The profile updates could not be applied.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="knowledge-modal-backdrop" role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget && !saving) onClose();
    }}>
      <section ref={dialogRef} tabIndex="-1" className="knowledge-modal knowledge-suggestion-modal" role="dialog" aria-modal="true" aria-labelledby="suggestion-title" aria-describedby={error ? 'knowledge-suggestion-error' : undefined}>
        <div className="knowledge-modal-header"><div><span className="say-eyebrow">Review first</span><h2 id="suggestion-title">Suggested profile updates</h2><p>From {source.title}. Only checked changes will be applied.</p></div><button type="button" className="knowledge-modal-close" aria-label="Close" onClick={onClose} disabled={saving}>×</button></div>
        <form onSubmit={submit} aria-busy={saving}>
          <div className="knowledge-suggestion-list">{availableFields.map(field => (
            <div key={field.key} className="knowledge-suggestion-item">
              <label className="knowledge-suggestion-toggle"><input type="checkbox" checked={selected.includes(field.key)} disabled={saving} onChange={() => setSelected(current => current.includes(field.key) ? current.filter(key => key !== field.key) : [...current, field.key])} /><strong>{field.label}</strong></label>
              <small>Current: {organization?.[field.organizationKey] || 'Not provided'}</small>
              {field.options ? <select value={values[field.key]} disabled={saving || !selected.includes(field.key)} onChange={event => setValues(current => ({ ...current, [field.key]: event.target.value }))}>{field.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select> : field.multiline ? <textarea rows="4" value={values[field.key]} disabled={saving || !selected.includes(field.key)} onChange={event => setValues(current => ({ ...current, [field.key]: event.target.value }))} /> : <input value={values[field.key]} disabled={saving || !selected.includes(field.key)} onChange={event => setValues(current => ({ ...current, [field.key]: event.target.value }))} />}
            </div>
          ))}</div>
          {error ? <div id="knowledge-suggestion-error"><Alert tone="danger">{error}</Alert></div> : null}
          <div className="knowledge-modal-actions"><Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button><Button type="submit" loading={saving} loadingLabel="Applying…" disabled={!selected.length}>{`Apply ${selected.length} update${selected.length === 1 ? '' : 's'}`}</Button></div>
        </form>
      </section>
    </div>
  );
}


export function SectionSuggestionReview({ section, entries, organization, onClose, onApply }) {
  const [selected, setSelected] = useState(() => entries.map(entry => entry.id));
  const [values, setValues] = useState(() => Object.fromEntries(
    entries.map(entry => [entry.id, entry.value]),
  ));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useDialogKeyboard(onClose, saving);

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onApply(entries.filter(entry => selected.includes(entry.id)).map(entry => ({
        ...entry,
        value: values[entry.id],
      })));
      onClose();
    } catch (requestError) {
      setError(knowledgeErrorMessage(requestError, 'The profile updates could not be applied.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="knowledge-modal-backdrop" role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget && !saving) onClose();
    }}>
      <section ref={dialogRef} tabIndex="-1" className="knowledge-modal knowledge-suggestion-modal" role="dialog" aria-modal="true" aria-labelledby="section-suggestion-title" aria-describedby={error ? 'section-suggestion-error' : undefined}>
        <div className="knowledge-modal-header"><div><span className="say-eyebrow">Review first</span><h2 id="section-suggestion-title">Apply {section.title} suggestions</h2><p>Review the suggested updates for this section. Only checked changes will be applied.</p></div><button type="button" className="knowledge-modal-close" aria-label="Close" onClick={onClose} disabled={saving}>×</button></div>
        <form onSubmit={submit} aria-busy={saving}>
          <div className="knowledge-suggestion-list">{entries.map(entry => (
            <div key={entry.id} className="knowledge-suggestion-item">
              <label className="knowledge-suggestion-toggle"><input type="checkbox" checked={selected.includes(entry.id)} disabled={saving} onChange={() => setSelected(current => current.includes(entry.id) ? current.filter(id => id !== entry.id) : [...current, entry.id])} /><strong>{entry.label}</strong></label>
              <small>From {entry.source.title} · Current: {organization?.[entry.organizationKey] || 'Not provided'}</small>
              {entry.options ? <select value={values[entry.id]} disabled={saving || !selected.includes(entry.id)} onChange={event => setValues(current => ({ ...current, [entry.id]: event.target.value }))}>{entry.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select> : entry.multiline ? <textarea rows="4" value={values[entry.id]} disabled={saving || !selected.includes(entry.id)} onChange={event => setValues(current => ({ ...current, [entry.id]: event.target.value }))} /> : <input value={values[entry.id]} disabled={saving || !selected.includes(entry.id)} onChange={event => setValues(current => ({ ...current, [entry.id]: event.target.value }))} />}
            </div>
          ))}</div>
          {error ? <div id="section-suggestion-error"><Alert tone="danger">{error}</Alert></div> : null}
          <div className="knowledge-modal-actions"><Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button><Button type="submit" loading={saving} loadingLabel="Applying…" disabled={!selected.length}>{`Apply ${selected.length} update${selected.length === 1 ? '' : 's'}`}</Button></div>
        </form>
      </section>
    </div>
  );
}
